import React from "react";

import {
    CATEGORY_OPACITY_MAX,
    CATEGORY_BORDER_STYLE_SVG,
    CATEGORY_BORDER_STYLE_UNI,
    CATEGORY_SVG_BORDER_WIDTH,
    CATEGORY_SVG_BORDER_SLICE,
} from "@constants/index";

import { DisplayStyleValues } from "@pages/GeneralConfig/Design/Content/OrderTaking/GenericInterface/Design/shared/DisplayStyleSettings";

// Source unique de la résolution de couleurs (sentinelles → hex), partagée
// avec les réglages catégorie/produit.
export { makeResolveColor } from "@pages/GeneralConfig/Design/Content/OrderTaking/GenericInterface/Design/shared/displayColor";

export const DEFAULT_DISPLAY_TEXT_COLOR = "#343434";
const HEX_REGEX = /^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Préfixe attendu d'un SVG de bordure : data URL base64 image/svg+xml, produit
// par DisplayStyleSettings APRÈS purge DOMPurify.
const SVG_BORDER_DATA_URL_PREFIX = "data:image/svg+xml;base64,";

// Charge utile autorisée APRÈS le préfixe : base64 pur (alphabet standard +
// padding `=`), sans parenthèse, espace ni caractère spécial. Vérifier le
// seul préfixe ne suffit pas : une valeur forgée comme
// `data:image/svg+xml;base64,xxx) url(javascript:alert(1)//` commence bien par
// le préfixe mais referme le `url()` CSS et injecte une 2e fonction. Exiger un
// corps base64 strict empêche cette évasion de `url(...)` dans border-image.
const BASE64_PAYLOAD_REGEX = /^[A-Za-z0-9+/]+=*$/;

// Type de marque (branded type) matérialisant la frontière de confiance : une
// valeur SafeSvgDataUrl a passé la garde safeGetSvgBorderCssUrl (préfixe SVG
// base64 vérifié) OU la purge DOMPurify à l'upload. Le compilateur empêche de
// confondre une string brute issue du store avec une data URL validée — utile
// pour un futur usage CSS `url()` / `src`. NE JAMAIS utiliser en innerHTML.
export type SafeSvgDataUrl = string & { readonly __brand: unique symbol };

// Garde-fou avant usage du champ svgBorder en CSS (border-image / url()).
// Retourne la data URL UNIQUEMENT si elle a bien le préfixe SVG base64 attendu,
// sinon "" (aucune bordure). Empêche qu'une valeur svgBorder corrompue — issue
// d'un template compromis ou d'un autre schéma (javascript:, data:text/html…) —
// soit injectée dans le rendu. Le champ svgBorder ne doit JAMAIS être inséré via
// innerHTML / dangerouslySetInnerHTML : seul un usage `src`/`url()` est sûr.
export function safeGetSvgBorderCssUrl(
    dataUrl: string | undefined
): SafeSvgDataUrl {
    // Null-guard : un template incomplet (ou un store non encore hydraté) peut
    // exposer svgBorder à undefined — on traite ce cas comme « pas de bordure ».
    // La chaîne vide est aussi une SafeSvgDataUrl (= absence de bordure sûre).
    if (
        typeof dataUrl !== "string" ||
        !dataUrl.startsWith(SVG_BORDER_DATA_URL_PREFIX)
    ) {
        return "" as SafeSvgDataUrl;
    }
    // Le préfixe seul est insuffisant : on exige que le reste soit du base64
    // pur (cf. BASE64_PAYLOAD_REGEX) — sinon une charge forgée pourrait
    // s'évader du `url(...)` injecté dans border-image.
    const payload = dataUrl.slice(SVG_BORDER_DATA_URL_PREFIX.length);
    if (!BASE64_PAYLOAD_REGEX.test(payload)) {
        return "" as SafeSvgDataUrl;
    }
    return dataUrl as SafeSvgDataUrl;
}

// Convertit un hex (#rgb / #rrggbb) en rgba() avec opacité en pourcentage.
// Garde : si ce n'est pas un hex valide (ex. déjà "rgb(...)", "transparent" ou
// vide), on renvoie la valeur telle quelle pour éviter un rgba(NaN, ...).
export function hexToRgba(
    hex: string | undefined,
    opacityPercent: number
): string {
    // Null-guard : couleur absente d'un template incomplet → on renvoie une
    // valeur CSS sûre (chaîne vide = pas de fond) plutôt que de crasher.
    if (typeof hex !== "string") {
        return "";
    }
    const sanitized = hex.replace("#", "");

    if (!HEX_REGEX.test(sanitized)) {
        return hex;
    }

    const full =
        sanitized.length === 3
            ? sanitized
                  .split("")
                  .map((c) => c + c)
                  .join("")
            : sanitized;

    const r = parseInt(full.substring(0, 2), 16);
    const g = parseInt(full.substring(2, 4), 16);
    const b = parseInt(full.substring(4, 6), 16);

    const alpha =
        Math.max(0, Math.min(100, opacityPercent)) / CATEGORY_OPACITY_MAX;

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Style produit effectif résolu (fond + couleur texte + bordure) à partir d'un
// jeu de valeurs déjà sélectionné (mode actif côté produit, ou jeu effectif
// côté étape produit composé) et d'un résolveur de sentinelles. Source UNIQUE
// partagée par les previews CardWrapper (interface générique) et
// MainContentWrapper (étape produit composé) — toute évolution du calcul de
// style se fait ici, évitant une divergence silencieuse entre les deux.
//
// Les appelants restent libres du RENDU de la bordure :
//   - `cardBorder` : CSSProperties prêtes à poser en inline sur la carte
//     (utilisé par MainContentWrapper).
//   - `isUniBorder` / `isSvgBorder` / `safeSvgBorderUrl` / `resolvedBorderColor`
//     : primitives pour un rendu en overlay (utilisé par CardWrapper).
export type ResolvedProductDisplayStyle = {
    background: string;
    textColor: string;
    resolvedBorderColor: string;
    safeSvgBorderUrl: SafeSvgDataUrl;
    isUniBorder: boolean;
    isSvgBorder: boolean;
    cardBorder: React.CSSProperties;
};

export function resolveProductDisplayStyle(
    values: DisplayStyleValues,
    resolveColor: (color: string) => string
): ResolvedProductDisplayStyle {
    const safeSvgBorderUrl = safeGetSvgBorderCssUrl(values.svgBorder);
    const resolvedBorderColor = resolveColor(values.borderColor);
    const isSvgBorder =
        values.borderStyle === CATEGORY_BORDER_STYLE_SVG &&
        safeSvgBorderUrl.length > 0;
    const isUniBorder = values.borderStyle === CATEGORY_BORDER_STYLE_UNI;

    const cardBorder: React.CSSProperties = isSvgBorder
        ? {
              borderStyle: "solid",
              borderWidth: `${CATEGORY_SVG_BORDER_WIDTH}px`,
              borderImage: `url(${safeSvgBorderUrl}) ${CATEGORY_SVG_BORDER_SLICE} stretch`,
          }
        : isUniBorder
        ? { border: `2px solid ${resolvedBorderColor}` }
        : {};

    return {
        background: hexToRgba(
            resolveColor(values.backgroundColor),
            values.backgroundOpacity
        ),
        textColor: resolveColor(values.selectedTextColor),
        resolvedBorderColor,
        safeSvgBorderUrl,
        isUniBorder,
        isSvgBorder,
        cardBorder,
    };
}
